<template>
  <!-- eslint-disable max-len  -->

  <div v-if="displayTOTP">
    <TOTPPanel
      :errorCode="resetInfo.errorCode"
      :processing="processing"
      @backupCodeSubmitted="onTOTPBackCodeSubmitted"
      @codeSubmitted="onTOTPCodeSubmitted"
    />
  </div>

  <div v-else-if="isKeyInvalid">
    <div v-if="isKeyExpired">
      {{ $t(LABEL_PASSWORD_RESET_KEY_EXPIRED, [passwordKeyExpiration]) }}
    </div>
    <div v-else>{{ $t(LABEL_PASSWORD_RESET_KEY_INVALID) }}</div>
    <div style="margin-top: 1rem">
      <a style="cursor: pointer" @click="$emit('retry-link')">{{ $t(LABEL_START_OVER) }}</a>
    </div>
  </div>

  <div v-else-if="passwordSuccess">
    <div style="margin-bottom: 0.5rem">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M20 0C31.0723 0 40 8.97756 40 20C40 31.0723 31.0723 40 20 40C8.97756 40 0 31.0723 0 20C0 8.97756 8.97756 0 20 0ZM14.8628 25.5362L8.57855 19.2519C8.52868 19.202 8.4788 19.202 8.42893 19.2519L6.48379 21.197C6.43392 21.2469 6.43392 21.3466 6.48379 21.3965L15.212 30.1746C15.2618 30.2244 15.3616 30.2244 15.4115 30.1746L33.5661 12.02C33.616 11.9701 33.616 11.8703 33.5661 11.8204L31.6209 9.87531C31.5711 9.82544 31.4713 9.82544 31.4214 9.87531L15.8105 25.5362C15.5611 25.7855 15.1122 25.7855 14.8628 25.5362ZM20 1.19701C9.62594 1.19701 1.19701 9.62594 1.19701 20C1.19701 30.4239 9.62594 38.8529 20 38.8529C30.4239 38.8529 38.8529 30.4239 38.8529 20C38.8529 9.62594 30.4239 1.19701 20 1.19701Z"
          fill="#27AE60"
        />
      </svg>
    </div>
    <div v-html="passwordSuccess"></div>
    <div style="margin-top: 1rem">
      <a :href="signInUrl">{{ signInMessage }}</a>
    </div>
  </div>

  <div v-show="showFormPanel" v-if="renderFormPanel" class="form-panel">
    <PanelHeader :titleText="$t(LABEL_PASSWORD_RESET)" :firstPanel="true" />
    <form autocomplete="off">
      <div class="form-group">
        <div class="form-label">{{ passwordLabel }}</div>
        <div class="form-control-group pwd-form-group" v-bind:class="{ 'is-danger': isPwdInvalid, active: passwordFocus }">
          <input
            :type="passwordFieldType"
            class="form-control"
            ref="passwordField"
            v-model="password"
            name="password"
            tabindex="0"
            autocomplete="new-password"
            :maxlength="this.passwordMaxLength"
            spellcheck="false"
            autocapitalize="off"
            autocorrect="off"
            @blur="onBlurPassword"
            @focus="onFocusPassword"
            @keyup="validPassword('k')"
            @keyup.enter="resetPassword"
          />
          <pwd-eye-icon v-on:click="onPasswordEyeClick" :visible="isPasswordVisible"></pwd-eye-icon>
        </div>
        <div class="help" v-bind:class="{ 'is-danger': isPwdInvalid }" v-if="passwordMessageLabel" v-html="passwordMessageLabel"></div>
      </div>

      <div class="form-group">
        <div class="form-label">{{ confirmLabel }}</div>
        <div class="form-control-group pwd-form-group" v-bind:class="{ 'is-danger': isPwdConfirmInvalid, active: confirmFocus }">
          <input
            :type="confirmFieldType"
            class="form-control"
            v-model="confirm"
            ref="passwordConfirmField"
            tabindex="0"
            autocomplete="new-password"
            :maxlength="this.passwordMaxLength"
            spellcheck="false"
            autocapitalize="off"
            autocorrect="off"
            @blur="onBlurConfirm"
            @focus="onFocusConfirm"
            @keyup="validPassword('k')"
            @keyup.enter="resetPassword"
          />
          <pwd-eye-icon v-on:click="onConfirmEyeClick" :visible="isConfirmVisible"></pwd-eye-icon>
        </div>
      </div>
    </form>

    <div class="btn-panel">
      <a
        class="btn btn-full"
        tabindex="0"
        v-bind:class="{ 'is-disabled': processing, 'is-loading': processing }"
        @keyup.enter="resetPassword"
        @click="resetPassword"
        >{{ $t(LABEL_PASSWORD_RESET) }}</a
      >
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { computed, defineComponent, PropType, ref, toRef, watchEffect } from 'vue';
import PanelHeader from '@/components/panel-header.vue';
import focus from '@/libs/focus';
import usePasswordValidation from '@/libs/passwordValidation';
import PwdEyeIcon from '@/components/pwd-eye-icon.vue';
import TOTPPanel from '@/components/totp-panel.vue';
import {
  LABEL_PASSWORD_RESET_KEY_INVALID,
  LABEL_PASSWORD_RESET_KEY_EXPIRED,
  LABEL_PASSWORD_RESET,
  LABEL_PASSWORD_RESET_HELP,
  LABEL_START_OVER,
  LABEL_YOUR_PASSWORD_HAS_BEEN_CHANGED,
  LABEL_CONFIRM,
} from '@/libs/i18nLabels';
import passwordState, { PasswordResetErrorCode, PasswordResetResult } from './passwordState';
import { LABEL_FIELD_INVALID, LABEL_PASSWORD, translateFormLabel } from '../libs/form';

export default defineComponent({
  components: {
    PanelHeader,
    PwdEyeIcon,
    TOTPPanel,
  },

  emits: ['retry-link'],
  props: {
    passwordMaxLength: {
      type: Number,
      required: true,
    },

    passwordKeyExpiration: {
      type: Number,
      required: true,
    },

    signInUrl: {
      type: String,
      required: true,
    },

    passwordMinLength: {
      type: Number,
      required: true,
    },

    resetInfo: {
      type: Object as PropType<PasswordResetResult>,
    },
  },

  setup(props) {
    const {
      password,
      passwordConfirm: confirm,
      isPasswordInvalid: isPwdInvalid,
      isPasswordConfirmInvalid: isPwdConfirmInvalid,
      passwordMessage,
      validate: validPassword,
    } = usePasswordValidation(props.passwordMinLength);

    const isKeyInvalid = ref(false);
    const isKeyExpired = ref(false);

    const displayTOTP = ref(false);

    const passwordSuccess = ref('');
    const signInMessage = ref('');

    function updateError(resetInfo: PasswordResetResult) {
      isKeyInvalid.value = false;
      isKeyExpired.value = false;

      // displayTOTP.value = false; //do not clear TOTP here

      if (resetInfo.errorCode) {
        displayTOTP.value = false;

        if (resetInfo.errorCode === PasswordResetErrorCode.TOTPRequired) {
          displayTOTP.value = true;
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.TOTPInvalidCode || resetInfo.errorCode === PasswordResetErrorCode.TOTPInvalidBackupCode) {
          displayTOTP.value = true;
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.InvalidPassword) {
          isPwdInvalid.value = true;
          passwordMessage.value = { label: LABEL_FIELD_INVALID, params: [{ label: LABEL_PASSWORD }] };
          return;
        }

        if (resetInfo.errorCode === PasswordResetErrorCode.ExpiredKey || PasswordResetErrorCode.InvalidKey) {
          isKeyInvalid.value = true;
          isKeyExpired.value = resetInfo.errorCode === PasswordResetErrorCode.ExpiredKey;
        }
      }
    }

    const resetInfo = toRef(props, 'resetInfo');

    watchEffect(() => {
      if (resetInfo.value) {
        updateError(resetInfo.value);
      }
    });

    // Fix issue chen firefox suggests to save password even if there's an error (expired link etc ..)
    // display: none : save password popup is not displayed
    // not rendered : the password popup is displayed
    const showFormPanel = computed(() => {
      return !isKeyInvalid.value && !displayTOTP.value;
    });

    const renderFormPanel = computed(() => {
      return isKeyInvalid.value || displayTOTP.value || !passwordSuccess.value;
    });

    return {
      password,
      confirm,
      isPwdInvalid,
      isPwdConfirmInvalid,
      passwordMessage,
      isKeyInvalid,
      isKeyExpired,
      validPassword,
      signInMessage,
      passwordSuccess,
      showFormPanel,
      renderFormPanel,
      displayTOTP,
      LABEL_PASSWORD_RESET_KEY_INVALID,
      LABEL_PASSWORD_RESET_KEY_EXPIRED,
      LABEL_PASSWORD_RESET,
      LABEL_PASSWORD_RESET_HELP,
      LABEL_START_OVER,
    };
  },

  data() {
    return {
      processing: false,

      passwordFieldType: 'password',
      passwordFocus: false,

      confirmFieldType: 'password',
      confirmFocus: false,

      passwordReseted: false,
    };
  },

  computed: {
    passwordLabel() {
      return this.$t(LABEL_PASSWORD).toString();
    },

    isPasswordVisible() {
      return this.passwordFieldType === 'text';
    },

    isConfirmVisible() {
      return this.confirmFieldType === 'text';
    },

    confirmLabel() {
      return this.$t(LABEL_CONFIRM).toString();
    },

    passwordMessageLabel() {
      if (!this.passwordMessage || !this.passwordMessage.label) {
        return '&nbsp;'; // maintains  space
      }

      return translateFormLabel(this.passwordMessage, this.$t);
    },
  },

  methods: {
    onBlurPassword() {
      this.passwordFocus = false;
      this.validPassword('b');
    },
    onFocusPassword() {
      this.passwordFocus = true;
    },

    onBlurConfirm() {
      this.confirmFocus = false;
      this.validPassword('b');
    },
    onFocusConfirm() {
      this.confirmFocus = true;
    },

    onPasswordEyeClick() {
      this.passwordFieldType = this.passwordFieldType === 'text' ? 'password' : 'text';
      setTimeout(() => {
        const pwdElement = this.$refs.passwordField as HTMLElement;
        pwdElement.focus();
      }, 1);
    },

    onConfirmEyeClick() {
      this.confirmFieldType = this.confirmFieldType === 'text' ? 'password' : 'text';
      setTimeout(() => {
        const pwdElement = this.$refs.passwordConfirmField as HTMLElement;
        pwdElement.focus();
      }, 1);
    },

    focusField() {
      focus(this.$refs.passwordField);
    },

    /**
     */

    resetPassword() {
      this.submitPassword();
    },

    async submitPassword(TOTPCode?: string, TOTPBackupCode?: string) {
      this.validPassword('');

      if (this.isPwdConfirmInvalid) {
        //  password confirm before password
        focus(this.$refs.passwordConfirmField);
      } else if (this.isPwdInvalid) {
        focus(this.$refs.passwordField);
      }

      if (this.isPwdInvalid) {
        this.processing = false;
        return;
      }

      this.processing = true;
      //
      const res = await passwordState.resetPassword({ password: this.password, TOTPBackupCode, TOTPCode }, this.$i18n);

      if (res && !res.errorCode) {
        this.displayTOTP = false; // TOTP can be displayed
        this.signInMessage = res.signinMessage || '';
        this.passwordSuccess = this.$t(LABEL_YOUR_PASSWORD_HAS_BEEN_CHANGED);
      }
      this.processing = false;
    },

    /**
     * TOTP
     */

    onTOTPCodeSubmitted(code: string) {
      this.submitPassword(code);
    },

    onTOTPBackCodeSubmitted(backupCode: string) {
      this.submitPassword(undefined, backupCode);
    },
  },

  mounted() {
    this.focusField();
  },
});
</script>

<style lang="scss" scoped>
.pwd-form-group {
  display: flex;
  align-items: center;

  .eyePwd {
    display: flex;
  }
}
</style>
